import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent 
} from '@/components/ui/chart';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  ResponsiveContainer,
  Tooltip,
  Legend 
} from 'recharts';
import { MetricCard } from './MetricCard';
import { generateMockAnalyticsData } from './mockData';
import { 
  TrendingUp, 
  FileText, 
  DollarSign, 
  Clock, 
  Target 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

const chartConfig = {
  quotes: {
    label: 'Total Quotes',
    color: 'hsl(var(--primary))',
  },
  accepted: {
    label: 'Accepted',
    color: 'hsl(var(--secondary))',
  },
  revenue: {
    label: 'Revenue',
    color: 'hsl(var(--primary))',
  },
  avgResponseTime: {
    label: 'Avg Response Time',
    color: 'hsl(var(--primary))',
  },
  target: {
    label: 'Target',
    color: 'hsl(var(--muted-foreground))',
  },
};

export const AnalyticsDashboard = () => {
  const data = generateMockAnalyticsData();

  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(value);

  const formatPercentage = (value: number) => `${value}%`;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'declined': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'interested': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <MetricCard
          title="Total Quotes"
          value={data.metrics.totalQuotes}
          icon={FileText}
          trend={{ value: 12.5, isPositive: true }}
          subtitle="vs last month"
        />
        <MetricCard
          title="Acceptance Rate"
          value={data.metrics.acceptanceRate}
          icon={Target}
          formatter={formatPercentage}
          trend={{ value: 3.2, isPositive: true }}
          subtitle="vs last month"
        />
        <MetricCard
          title="Avg Quote Value"
          value={data.metrics.averageQuoteValue}
          icon={DollarSign}
          formatter={formatCurrency}
          trend={{ value: 8.1, isPositive: true }}
          subtitle="vs last month"
        />
        <MetricCard
          title="Monthly Revenue"
          value={data.metrics.monthlyRevenue}
          icon={TrendingUp}
          formatter={formatCurrency}
          trend={{ value: 5.4, isPositive: true }}
          subtitle="vs last month"
        />
        <MetricCard
          title="Avg Response Time"
          value={`${data.metrics.averageResponseTime} days`}
          icon={Clock}
          trend={{ value: 0.8, isPositive: false }}
          subtitle="vs last month"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Quote Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Quote Performance Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.quotePerformance} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="quotes" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    name="Total Quotes"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="accepted" 
                    stroke="hsl(var(--secondary))" 
                    strokeWidth={2}
                    name="Accepted Quotes"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Quote Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Quote Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <Pie
                    data={data.statusDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, percentage }) => `${status}: ${percentage}%`}
                  >
                    {data.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Revenue Growth</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.revenueData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${value / 1000}k`} />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Bar 
                    dataKey="revenue" 
                    fill="hsl(var(--primary))" 
                    name="Revenue"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Response Time Analytics */}
        <Card>
          <CardHeader>
            <CardTitle>Response Time vs Target</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.responseTimeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="avgResponseTime" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    name="Avg Response Time"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="hsl(var(--muted-foreground))" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Target"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Industry Breakdown & Geographic Distribution */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Industry Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-[300px] overflow-y-auto">
              {data.industryBreakdown.map((item) => (
                <div key={item.industry} className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate flex-1 mr-4">{item.industry}</span>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className="text-sm text-muted-foreground">{item.quotes} quotes</span>
                    <span className="text-sm font-medium">{item.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top States by Quotes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-[300px] overflow-y-auto">
              {data.geographicData.slice(0, 5).map((item) => (
                <div key={item.state} className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate flex-1 mr-4">{item.state}</span>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className="text-sm text-muted-foreground">{item.quotes} quotes</span>
                    <span className="text-sm font-medium">{formatCurrency(item.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Quotes Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Quotes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Company</th>
                  <th className="text-left py-2">Industry</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Value</th>
                  <th className="text-left py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recentQuotes.map((quote) => (
                  <tr key={quote.id} className="border-b">
                    <td className="py-2 font-medium">{quote.companyName}</td>
                    <td className="py-2 text-muted-foreground">{quote.industry}</td>
                    <td className="py-2">
                      <Badge 
                        variant="secondary" 
                        className={getStatusColor(quote.status)}
                      >
                        {quote.status}
                      </Badge>
                    </td>
                    <td className="py-2 text-right font-medium">
                      {formatCurrency(quote.value)}
                    </td>
                    <td className="py-2 text-muted-foreground">{quote.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};